import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import dynamic from 'next/dynamic';
import { ResidenceCard } from '../../Cards/ResidencesCard';
import { ResidenceSort, ResidencesQuery } from '../../../graphql/graphql';
import { BusinessItemsListPageWrapper } from '../../BusinessItemsListPage';
import { BusinessItemsType } from '../../BusinessItemsListPage/types';
import { residencesQuery } from '../../../api/queries';
import { ResidencesListProps, SquareFilterEnum } from './types';
import { FilterContent, FiltersWrapper } from './styled';
import { normalizeResidencesSortForUrl } from '../../../utils/helpers/normalizeResidencesSortForUrl';
import { SortingPlaceholder } from '../../Residences/SortingFilter/styled';
import { SquareFilterPlaceholder } from '../../Residences/SquareFilter';
import { CardGridStyled } from '../../../styled/cardGrid.styled';
import { ItemsViewToggleLink } from '../../NewSearch/NewSearchBar/ItemsViewToggleLink';
import { MapViewQueryParam } from '../../ItemsMap/types';
import { ResidencesMap } from '../../Residences/ResidencesMap';
import { useGetIsMapView } from '../../../hooks/useGetIsMapView';

const ResidencesSorting = dynamic(
  () =>
    import('../../Residences/SortingFilter').then(mod => mod.ResidencesSorting),
  { ssr: false, loading: SortingPlaceholder }
);

const SquareFilter = dynamic(
  () => import('../../Residences/SquareFilter').then(mod => mod.SquareFilter),
  { ssr: false, loading: () => <SquareFilterPlaceholder /> }
);

export const ResidencesList: React.FC<ResidencesListProps> = ({
  residences,
  hasError,
  sort
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const isMapView = useGetIsMapView(MapViewQueryParam.residencesView);

  const [sortingValue, setSortingValue] = useState(sort);
  const [perSqrFilter, setPerSqrFilter] = useState(SquareFilterEnum.all);

  const { loading, data, error } = useQuery<ResidencesQuery>(residencesQuery, {
    variables: { sort: sortingValue },
    ssr: false,
    skip: sort === sortingValue
  });

  useEffect(() => {
    const newSortingValue = router.query.sort
      ? (String(router.query.sort).toUpperCase() as ResidenceSort)
      : ResidenceSort.DateDesc;

    if (sortingValue !== newSortingValue) setSortingValue(newSortingValue);
  }, [router]);

  useEffect(() => {
    const newSortingValue = normalizeResidencesSortForUrl(
      SquareFilterEnum.all,
      sort
    );

    if (newSortingValue && newSortingValue !== sort) {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            sort: newSortingValue.toLowerCase()
          }
        },
        undefined,
        { shallow: true }
      );
    }
  }, []);

  const isPerUnit = perSqrFilter === SquareFilterEnum.perSqr;
  const listData = data?.residences || residences || [];

  const itemsWithProps = useMemo(
    () =>
      listData.map(item => ({
        ...item,
        showPerUnit: isPerUnit,
        cypress: 'residence',
        stat: 'complex-card'
      })),
    [listData, isPerUnit]
  );

  return (
    <BusinessItemsListPageWrapper
      title={t('sections.residences.title')}
      hasError={hasError || !!error}
      items={itemsWithProps}
      CardComponent={ResidenceCard}
      ListContainer={CardGridStyled}
      type={BusinessItemsType.residences}
      loading={loading}
      isMapView={isMapView}
      mapView={<ResidencesMap isPerUnit={isPerUnit} />}
    >
      <FiltersWrapper>
        <SquareFilter
          selectedValue={perSqrFilter}
          onChange={setPerSqrFilter}
          sortingValue={sortingValue}
        />
        <FilterContent>
          <ItemsViewToggleLink
            isMapView={isMapView}
            query={MapViewQueryParam.residencesView}
            cypress="residences-map-link"
            stat="view-on-map"
          />
          <ResidencesSorting
            sortingValue={sortingValue}
            isPerUnit={isPerUnit}
            disabled={isMapView}
          />
        </FilterContent>
      </FiltersWrapper>
    </BusinessItemsListPageWrapper>
  );
};
